import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";
import { HiOutlineRefresh } from "react-icons/hi";
import ButtonBar from "../../Elements/ButtonBar";
import { UserContext } from "../../../context/UserContext";
import { useIntervaloVisible } from "../../../lib/hooks/useIntervaloVisible";

const POLL_INTERVAL_MS = 30_000;

// ─── Environment definitions ──────────────────────────────────────────────────
const ENVS = [
  {
    key: "prod",
    label: "Production",
    appUrl: "https://clubsagent.ai",
    apiUrl: "https://api.clubsagent.ai",
    logsUrl:
      "https://eu-west-2.console.aws.amazon.com/ecs/v2/clusters/alameda-prod-cluster/services/alameda-prod-backend/logs",
  },
  {
    key: "preview",
    label: "Preview",
    appUrl: "https://preview.clubsagent.ai",
    apiUrl: "https://api-preview.clubsagent.ai",
    logsUrl:
      "https://eu-west-2.console.aws.amazon.com/ecs/v2/clusters/alameda-prod-cluster/services/alameda-prod-backend-preview/logs",
  },
  {
    key: "staging",
    label: "Staging",
    appUrl: "https://weedagent.eu",
    apiUrl: "https://api.weedagent.eu",
    logsUrl: "https://weedagent.eu/admin",
  },
];

// ─── Quick dispatch actions ───────────────────────────────────────────────────
const DISPATCH_ACTIONS = [
  {
    label: "Deploy Preview (BE)",
    repo: "backend",
    workflow: "deploy-production.yml",
    inputs: { release_mode: "deploy_preview" },
    color: "yellow",
  },
  {
    label: "Deploy Preview (FE)",
    repo: "frontend",
    workflow: "deploy.yml",
    inputs: { release_mode: "deploy_preview" },
    color: "yellow",
  },
  {
    label: "Promote Prod (BE)",
    repo: "backend",
    workflow: "deploy-production.yml",
    inputs: { release_mode: "promote_prod" },
    color: "red",
  },
  {
    label: "Promote Prod (FE)",
    repo: "frontend",
    workflow: "deploy.yml",
    inputs: { release_mode: "promote_prod" },
    color: "red",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function classifyRun(run) {
  const name = (run.name || "").toLowerCase();
  // The deploy workflow names runs "<repo> [<mode>] — …", so match the mode
  // token directly. Frontend preview deploys are 'push' events (not
  // workflow_dispatch), and the previous event-based check misread them as CI —
  // which broke the FE preview/prod SHA detection in the deploy panel.
  if (name.includes("promote_prod")) return "promote_prod";
  if (name.includes("deploy_preview")) return "deploy_preview";
  // Fallback for runs without the mode token.
  const event = run.event || "";
  if (event !== "workflow_dispatch") return "ci";
  if (name.includes("promote") || name.includes("production → prod"))
    return "promote_prod";
  return "deploy_preview";
}

function conclusionColor(conclusion, status) {
  if (status === "in_progress" || status === "queued") return "yellow";
  if (conclusion === "success") return "green";
  if (conclusion === "failure") return "red";
  if (conclusion === "cancelled") return "gray";
  return "gray";
}

function ecsHealth(svc) {
  if (!svc || svc.error) return { label: "Unknown", cls: "text-gray-500" };
  if (svc.running_count === svc.desired_count && svc.desired_count > 0)
    return { label: "Healthy", cls: "text-emerald-600 font-semibold" };
  return { label: "Degraded", cls: "text-red-600 font-semibold" };
}

function formatDuration(createdAt, updatedAt) {
  if (!createdAt || !updatedAt) return "—";
  const diffMs = new Date(updatedAt) - new Date(createdAt);
  const mins = Math.floor(diffMs / 60000);
  const secs = Math.floor((diffMs % 60000) / 1000);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Small reusable atoms (same style as CajasPage) ──────────────────────────
// eslint-disable-next-line no-unused-vars
function SummaryCard({ label, value, tone = "blue", sub, links }) {
  const tones = {
    blue: "border-blue-200 bg-blue-50 text-blue-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-red-200 bg-red-50 text-red-900",
    gray: "border-gray-200 bg-gray-50 text-gray-700",
  };
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${tones[tone] || tones.blue}`}>
      <div className="text-sm font-medium opacity-80">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-0.5 text-sm opacity-70">{sub}</div>}
      {links && (
        <div className="mt-2 flex gap-2">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              className="rounded bg-white/60 px-2 py-0.5 text-xs font-medium hover:bg-white/90 transition"
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ conclusion, status }) {
  const color = conclusionColor(conclusion, status);
  const map = {
    green: "bg-emerald-100 text-emerald-800",
    red: "bg-red-100 text-red-800",
    yellow: "bg-amber-100 text-amber-800",
    gray: "bg-gray-100 text-gray-700",
  };
  const label =
    status === "in_progress"
      ? "Running"
      : status === "queued"
      ? "Queued"
      : conclusion || "—";
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${map[color]}`}>
      {label}
    </span>
  );
}

function RepoBadge({ repo }) {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
        repo === "backend"
          ? "bg-purple-100 text-purple-700"
          : "bg-indigo-100 text-indigo-700"
      }`}
    >
      {repo}
    </span>
  );
}

function PrStateBadge({ merged, state }) {
  if (merged) return <span className="rounded-full px-2.5 py-1 text-xs font-semibold bg-purple-100 text-purple-700">merged</span>;
  if (state === "open") return <span className="rounded-full px-2.5 py-1 text-xs font-semibold bg-blue-100 text-blue-700">open</span>;
  return <span className="rounded-full px-2.5 py-1 text-xs font-semibold bg-gray-200 text-gray-600">closed</span>;
}

function EnvTargetBadge({ base }) {
  if (base === "main")
    return (
      <span className="rounded-full px-2.5 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700">
        → Preview → Prod
      </span>
    );
  if (base === "develop")
    return (
      <span className="rounded-full px-2.5 py-1 text-xs font-semibold bg-sky-100 text-sky-700">
        → Staging
      </span>
    );
  return null;
}

function PipelineDot({ conclusion, status, url, label }) {
  const color = conclusionColor(conclusion, status);
  const dotMap = {
    green: "bg-emerald-500",
    red: "bg-red-500",
    yellow: "bg-amber-400 animate-pulse",
    gray: "bg-gray-300",
  };
  const textMap = {
    green: "text-emerald-700",
    red: "text-red-700",
    yellow: "text-amber-700",
    gray: "text-gray-400",
  };
  const text =
    status === "in_progress"
      ? "Running"
      : status === "queued"
      ? "Queued"
      : conclusion || "—";
  return (
    <div className="flex flex-col items-center gap-1 min-w-[90px]">
      <div className={`flex items-center gap-1.5 text-sm font-semibold ${textMap[color]}`}>
        <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${dotMap[color]}`} />
        {url ? (
          <a href={url} target="_blank" rel="noreferrer" className="hover:underline">
            {text}
          </a>
        ) : (
          <span>{text}</span>
        )}
      </div>
      <span className="text-xs text-gray-400">{label}</span>
    </div>
  );
}

function Arrow() {
  return <span className="text-gray-300 text-base self-center mx-1">→</span>;
}

function Pagination({ page, pageSize, total, onPrev, onNext }) {
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  const totalPages = Math.ceil(total / pageSize);
  if (total <= pageSize) return null;
  return (
    <div className="flex items-center justify-between border-t border-gray-200 pt-3 mt-2">
      <span className="text-sm text-gray-500">
        {from}–{to} of {total}
      </span>
      <div className="flex gap-2">
        <button
          onClick={onPrev}
          disabled={page === 0}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ← Prev
        </button>
        <span className="flex items-center px-2 text-sm text-gray-500">
          {page + 1} / {totalPages}
        </span>
        <button
          onClick={onNext}
          disabled={(page + 1) * pageSize >= total}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

// ─── Pipeline card ────────────────────────────────────────────────────────────
function PipelineCard({ pr, allRuns, ecs, onMerge, merging }) {
  const prRuns = allRuns
    .filter(
      (r) =>
        r.repo === pr.repo &&
        (r.head_sha === pr.head_sha || r.head_branch === pr.head)
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const stageRuns = {};
  for (const run of prRuns) {
    const stage = classifyRun(run);
    if (!stageRuns[stage]) stageRuns[stage] = run;
  }

  const ecsEnv =
    pr.merged && pr.base === "main"
      ? ecs?.prod
      : pr.merged && pr.base === "develop"
      ? ecs?.preview
      : null;
  const ecsH = ecsEnv ? ecsHealth(ecsEnv) : null;

  const isOpen = pr.state === "open";

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <RepoBadge repo={pr.repo} />
          <span className="text-sm text-gray-400 font-mono font-medium">#{pr.number}</span>
          <a
            href={pr.url}
            target="_blank"
            rel="noreferrer"
            className="text-base font-semibold text-gray-800 hover:text-blue-600 hover:underline truncate max-w-lg"
            title={pr.title}
          >
            {pr.title}
          </a>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <PrStateBadge merged={pr.merged} state={pr.state} />
          {isOpen && (
            <button
              onClick={() => onMerge(pr)}
              disabled={merging === pr.number}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-500 bg-emerald-500 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50 transition"
            >
              {merging === pr.number ? "Merging…" : "Merge"}
            </button>
          )}
        </div>
      </div>

      {/* Branch info */}
      <div className="flex items-center gap-2 flex-wrap text-sm text-gray-500 mb-3">
        <span className="font-mono rounded bg-white border border-gray-200 px-2 py-0.5 text-gray-700">{pr.head}</span>
        <span className="text-gray-300">→</span>
        <span className="font-mono rounded bg-white border border-gray-200 px-2 py-0.5 text-gray-700">{pr.base}</span>
        <EnvTargetBadge base={pr.base} />
        <span className="text-gray-400">by {pr.author}</span>
        <span className="text-gray-400">{timeAgo(pr.updated_at)}</span>
      </div>

      {/* Pipeline stages — vary by target branch */}
      <div className="flex items-center gap-1 flex-wrap rounded-lg border border-gray-200 bg-white px-4 py-3">
        {/* PR state */}
        <div className="flex flex-col items-center gap-1 min-w-[70px]">
          <div className={`flex items-center gap-1.5 text-sm font-semibold ${isOpen ? "text-blue-600" : pr.merged ? "text-purple-600" : "text-gray-500"}`}>
            <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${isOpen ? "bg-blue-400" : pr.merged ? "bg-purple-400" : "bg-gray-300"}`} />
            {pr.merged ? "merged" : isOpen ? "open" : "closed"}
          </div>
          <span className="text-xs text-gray-400">PR</span>
        </div>
        <Arrow />

        {/* CI / Build — always shown */}
        <PipelineDot conclusion={stageRuns.ci?.conclusion} status={stageRuns.ci?.status} url={stageRuns.ci?.url} label="CI / Build" />
        <Arrow />

        {pr.base === "develop" ? (
          /* develop → Staging auto-deploy via Dokploy */
          <div className="flex flex-col items-center gap-1 min-w-[90px]">
            <div className={`flex items-center gap-1.5 text-sm font-semibold ${pr.merged ? "text-sky-700" : "text-gray-400"}`}>
              <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${pr.merged ? "bg-sky-500" : "bg-gray-300"}`} />
              {pr.merged ? "deployed" : "pending"}
            </div>
            <span className="text-xs text-gray-400">Staging (Dokploy)</span>
          </div>
        ) : (
          /* main → Preview → Prod */
          <>
            <PipelineDot conclusion={stageRuns.deploy_preview?.conclusion} status={stageRuns.deploy_preview?.status} url={stageRuns.deploy_preview?.url} label="Deploy Preview" />
            <Arrow />
            <PipelineDot conclusion={stageRuns.promote_prod?.conclusion} status={stageRuns.promote_prod?.status} url={stageRuns.promote_prod?.url} label="Promote Prod" />
            {ecsH && (
              <>
                <Arrow />
                <div className="flex flex-col items-center gap-1 min-w-[70px]">
                  <div className={`flex items-center gap-1.5 text-sm font-semibold ${ecsH.cls}`}>
                    <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${ecsH.label === "Healthy" ? "bg-emerald-500" : "bg-red-500"}`} />
                    {ecsH.label}
                  </div>
                  <span className="text-xs text-gray-400">ECS Live</span>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Run list */}
      {prRuns.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-gray-200 pt-3">
          {prRuns.slice(0, 5).map((run) => (
            <div key={run.id} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StatusBadge conclusion={run.conclusion} status={run.status} />
                <a href={run.url} target="_blank" rel="noreferrer"
                  className="text-sm text-gray-600 hover:text-blue-600 hover:underline">
                  {run.name}
                </a>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>{formatDuration(run.created_at, run.updated_at)}</span>
                <span>{timeAgo(run.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function OpsPage() {
  const [token] = useContext(UserContext);
  const [status, setStatus] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionStates, setActionStates] = useState({});
  const [syncPrStates, setSyncPrStates] = useState({});
  const [syncPrUrls, setSyncPrUrls] = useState({});
  const [syncPrPending, setSyncPrPending] = useState({});
  const [trackerRepo, setTrackerRepo] = useState("all");
  const [trackerPr, setTrackerPr] = useState(null);
  const [trackerLifecycle, setTrackerLifecycle] = useState(null);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [mergingPr, setMergingPr] = useState(null);
  const [activeTab, setActiveTab] = useState("pipeline");
  const [prPage, setPrPage] = useState(0);
  const [runPage, setRunPage] = useState(0);
  const [awsEnv, setAwsEnv] = useState("prod");
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [awsActionState, setAwsActionState] = useState({});
  const [dbSnapshot, setDbSnapshot] = useState(null);
  const [dbSnapshotLoading, setDbSnapshotLoading] = useState(false);
  const [dbSnapshotLabel, setDbSnapshotLabel] = useState("predeploy");

  const PR_PAGE_SIZE = 10;
  const RUN_PAGE_SIZE = 15;

  // Un error NO se esfuma solo. El resto sí: un «hecho» leído es basura en pantalla.
  //
  // Del incidente del 13-09-2026: el panel enseñó un fallo de GitHub durante cinco
  // segundos y luego no quedó nada —ni en pantalla ni en CloudWatch— con lo que el
  // diagnóstico empezó de cero. Un mensaje de error es una prueba: se queda hasta que
  // alguien lo cierra, y se puede copiar para pegarlo donde haga falta.
  const toastTimer = useRef(null);

  const showToast = useCallback((msg, variant = "info") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, variant });
    if (variant !== "error") {
      toastTimer.current = setTimeout(() => setToast(null), 5000);
    }
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(null);
  }, []);

  // Sin esto, un toast programado sobrevive al desmontaje y avisa sobre un panel que
  // ya no está.
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await fetch("/v1/ops/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setStatus(data);
      setLastRefreshed(new Date());
      setError(null);
      setPrPage(0);
      setRunPage(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Con la pestaña en segundo plano NO se pregunta: este panel se queda abierto
  // horas y cada llamada retenía una conexión de BD durante el fan-out a GitHub y
  // AWS (BE #612). Al volver a mirarlo, refresco inmediato.
  useIntervaloVisible(fetchStatus, POLL_INTERVAL_MS);

  const fetchLogs = useCallback(async (env, lines = 100) => {
    if (!token) return;
    setLogsLoading(true);
    setLogsError(null);
    try {
      const resp = await fetch(`/v1/ops/ecs/logs?env=${env}&lines=${lines}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setLogs(data);
    } catch (err) {
      setLogsError(err.message);
    } finally {
      setLogsLoading(false);
    }
  }, [token]);

  const handleEcsAction = useCallback(async (action, body) => {
    setAwsActionState((s) => ({ ...s, [action]: "loading" }));
    try {
      const resp = await fetch(`/v1/ops/ecs/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      setAwsActionState((s) => ({ ...s, [action]: "done" }));
      showToast(`${action} dispatched`, "success");
      setTimeout(() => {
        setAwsActionState((s) => ({ ...s, [action]: null }));
        fetchStatus();
      }, 3000);
    } catch (err) {
      setAwsActionState((s) => ({ ...s, [action]: null }));
      showToast(`${action} failed: ${err.message}`, "error");
    }
  }, [token, fetchStatus, showToast]);

  const pollDbSnapshot = useCallback(async (snapshotId) => {
    const resp = await fetch(
      `/v1/ops/rds/snapshot?snapshot_id=${encodeURIComponent(snapshotId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${resp.status}`);
    }
    return resp.json();
  }, [token]);

  const handleCreateDbSnapshot = useCallback(async () => {
    const label = (dbSnapshotLabel || "predeploy").trim() || "predeploy";
    setDbSnapshotLoading(true);
    try {
      const resp = await fetch("/v1/ops/rds/snapshot", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ label }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setDbSnapshot(data);
      showToast(`Snapshot started: ${data.snapshot_id}`, "success");
    } catch (err) {
      showToast(`DB snapshot failed: ${err.message}`, "error");
    } finally {
      setDbSnapshotLoading(false);
    }
  }, [token, dbSnapshotLabel, showToast]);

  useEffect(() => {
    if (!token || !dbSnapshot?.snapshot_id) return undefined;
    if (dbSnapshot.status === "available" || dbSnapshot.status === "failed") return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const data = await pollDbSnapshot(dbSnapshot.snapshot_id);
        if (cancelled) return;
        setDbSnapshot(data);
        if (data.status === "available") {
          showToast("DB snapshot ready", "success");
        } else if (data.status === "failed") {
          showToast("DB snapshot failed in RDS", "error");
        }
      } catch (_err) {
        // Keep polling — transient errors are common while snapshot is starting.
      }
    };

    tick();
    const id = setInterval(tick, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, dbSnapshot?.snapshot_id, dbSnapshot?.status, pollDbSnapshot, showToast]);

  const handleDispatch = useCallback(
    async (action) => {
      const key = `${action.repo}-${action.workflow}-${JSON.stringify(action.inputs)}`;
      setActionStates((s) => ({ ...s, [key]: "loading" }));
      try {
        const resp = await fetch("/v1/ops/dispatch", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            repo: action.repo,
            workflow_file: action.workflow,
            inputs: action.inputs,
          }),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          const detail = Array.isArray(body.detail)
            ? body.detail.map((e) => e.msg || JSON.stringify(e)).join("; ")
            : body.detail || `HTTP ${resp.status}`;
          throw new Error(detail);
        }
        setActionStates((s) => ({ ...s, [key]: "done" }));
        showToast(`Dispatched: ${action.label}`, "success");
        setTimeout(() => {
          setActionStates((s) => ({ ...s, [key]: null }));
          fetchStatus();
        }, 3000);
      } catch (err) {
        setActionStates((s) => ({ ...s, [key]: "error" }));
        showToast(`Error: ${err.message}`, "error");
        setTimeout(() => setActionStates((s) => ({ ...s, [key]: null })), 3000);
      }
    },
    [token, fetchStatus, showToast]
  );

  const handleSyncPr = useCallback(
    async (repo) => {
      setSyncPrStates((s) => ({ ...s, [repo]: "loading" }));
      try {
        const resp = await fetch("/v1/ops/sync-pr", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ repo }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const detail = Array.isArray(body.detail)
            ? body.detail.map((e) => e.msg || JSON.stringify(e)).join("; ")
            : body.detail || `HTTP ${resp.status}`;
          throw new Error(detail);
        }
        setSyncPrStates((s) => ({ ...s, [repo]: "done" }));
        const linkUrl = body.html_url || body.search_url;
        if (linkUrl) setSyncPrUrls((s) => ({ ...s, [repo]: linkUrl }));
        if (body.pending) setSyncPrPending((s) => ({ ...s, [repo]: true }));
        const label = body.existed
          ? "Sync PR already open — click the link"
          : body.pending
          ? "Workflow triggered — PR is being created (~1 min)"
          : "Sync PR created — click the link";
        showToast(label, body.pending ? "info" : "success");
        setTimeout(() => setSyncPrStates((s) => ({ ...s, [repo]: null })), 8000);
      } catch (err) {
        setSyncPrStates((s) => ({ ...s, [repo]: "error" }));
        showToast(`Sync PR failed: ${err.message}`, "error");
        setTimeout(() => setSyncPrStates((s) => ({ ...s, [repo]: null })), 3000);
      }
    },
    [token, showToast]
  );

  const fetchLifecycle = useCallback(
    async (pr) => {
      setTrackerPr(pr);
      setTrackerLifecycle(null);
      setTrackerLoading(true);
      try {
        const resp = await fetch(
          `/v1/ops/pr-lifecycle?repo=${pr.repo}&pr_number=${pr.number}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        setTrackerLifecycle(await resp.json());
      } catch (err) {
        showToast(`Lifecycle fetch failed: ${err.message}`, "error");
      } finally {
        setTrackerLoading(false);
      }
    },
    [token, showToast]
  );

  const handleMergePr = useCallback(
    async (pr) => {
      if (
        !window.confirm(
          `Merge PR #${pr.number} "${pr.title}" (${pr.repo})?\n\nThis cannot be undone.`
        )
      )
        return;
      setMergingPr(pr.number);
      try {
        const resp = await fetch("/v1/ops/merge-pr", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            repo: pr.repo,
            pr_number: pr.number,
            merge_method: "squash",
          }),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${resp.status}`);
        }
        showToast(`PR #${pr.number} merged successfully`, "success");
        setTimeout(fetchStatus, 2000);
      } catch (err) {
        showToast(`Merge failed: ${err.message}`, "error");
      } finally {
        setMergingPr(null);
      }
    },
    [token, fetchStatus, showToast]
  );

  const ecs = status?.ecs || {};
  const allPrs = (status?.prs || []).sort(
    (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
  );
  // Fallos de consulta (GitHub/AWS). Antes se tragaban y las pestañas salían
  // vacías: idénticas a "no hay actividad". Un token caducado costó una tarde de
  // diagnóstico creyendo que el deploy estaba roto (17-ago).
  const fallos = status?.errors || [];
  const runs = status?.runs || {};
  const allRuns = [...(runs.backend || []), ...(runs.frontend || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const TABS = [
    { key: "pipeline", label: `Pipeline (${allPrs.length})` },
    { key: "actions", label: "Quick Actions" },
    { key: "runs", label: `CI Runs (${allRuns.length})` },
    { key: "envs", label: "Environments" },
    { key: "aws", label: "AWS" },
    { key: "tracker", label: "Tracker" },
  ];

  const headerButtons = [
    {
      label: loading ? "Refreshing…" : "Refresh",
      color: "blue",
      onClick: fetchStatus,
    },
  ];

  return (
    <div className="space-y-4">
      {/* ── Header bar (matches ButtonBar style) ── */}
      <ButtonBar
        title={`Deploy Panel${lastRefreshed ? `  ·  ${lastRefreshed.toLocaleTimeString()}` : ""}`}
        buttons={headerButtons}
      />

      {/* ── Consultas caídas: lo que no se pudo leer, dicho a la cara ── */}
      {fallos.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <span aria-hidden>⚠️</span>
            No se pudo consultar {fallos.map((f) => f.fuente).join(" · ")}
          </div>
          <p className="mt-1 text-xs text-amber-800">
            Lo que ves abajo está incompleto: las listas vacías NO significan "sin actividad".
            Si falla GitHub, suele ser el <code>GITHUB_TOKEN</code> caducado en la configuración
            del backend de producción.
          </p>
          {fallos.map((f, i) => (
            <p key={i} className="mt-1 font-mono text-[11px] text-amber-700">{f.fuente}: {f.detalle}</p>
          ))}
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div
          role={toast.variant === "error" ? "alert" : "status"}
          className={`fixed top-6 right-6 z-50 max-w-md px-5 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${
            toast.variant === "success"
              ? "bg-emerald-600"
              : toast.variant === "error"
              ? "bg-red-600"
              : "bg-gray-800"
          }`}
        >
          {/* `break-words` porque un error de GitHub trae URLs y un request-id sin
              espacios: sin esto una sola «palabra» estira la caja fuera de la pantalla. */}
          <div className="flex items-start gap-3">
            <span className="flex-1 whitespace-pre-wrap break-words">{toast.msg}</span>
            {toast.variant === "error" && (
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(toast.msg)}
                  className="rounded px-2 py-0.5 text-xs underline underline-offset-2 hover:bg-white/20"
                  title="Copiar el error para pegarlo en un issue o pasárselo a soporte"
                >
                  Copiar
                </button>
                <button
                  type="button"
                  onClick={dismissToast}
                  aria-label="Cerrar el aviso"
                  className="rounded px-2 py-0.5 text-base leading-none hover:bg-white/20"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Error banner ── */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load status: {error}
        </div>
      )}

      {/* ── Environment summary cards ── */}
      <div className="grid gap-3 md:grid-cols-3">
        {ENVS.map((env) => {
          const svc = ecs[env.key];
          const health = ecsHealth(svc);
          const runningTask = svc?.tasks?.[0];
          const imageTag = runningTask?.image_tag;

          // Last PR deployed to this env
          const targetBase = env.key === "staging" ? "develop" : "main";
          const lastPr = allPrs.find(
            (p) => p.merged && p.base === targetBase
          );
          // For prod vs preview, also look at CI runs to distinguish
          const feDeployStage = env.key === "prod" ? "promote_prod" : env.key === "preview" ? "deploy_preview" : null;
          const lastDeployRun = feDeployStage
            ? allRuns.find((r) => classifyRun(r) === feDeployStage && r.conclusion === "success")
            : null;

          const healthColors = {
            Healthy: "border-emerald-200 bg-emerald-50",
            Degraded: "border-red-200 bg-red-50",
            Unknown: "border-gray-200 bg-gray-50",
          };
          const healthTextColors = {
            Healthy: "text-emerald-700",
            Degraded: "text-red-700",
            Unknown: "text-gray-500",
          };

          return (
            <div key={env.key} className={`rounded-xl border p-4 shadow-sm ${healthColors[health.label] || healthColors.Unknown}`}>
              {/* Header row */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">{env.label}</span>
                <span className={`text-sm font-bold ${healthTextColors[health.label] || "text-gray-500"}`}>{health.label}</span>
              </div>

              {/* Tasks */}
              {svc && !svc.error ? (
                <div className="text-xl font-bold text-gray-800 mb-2">
                  {svc.running_count}/{svc.desired_count} tasks
                </div>
              ) : (
                <div className="text-sm text-gray-400 mb-2">{env.key === "staging" ? "Dokploy" : "no ECS data"}</div>
              )}

              {/* Running image tag */}
              {imageTag && (
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-xs text-gray-500">Image:</span>
                  <span className="font-mono text-xs bg-white border border-indigo-200 text-indigo-700 rounded px-1.5 py-0.5">{imageTag}</span>
                  {runningTask?.started_at && (
                    <span className="text-xs text-gray-400">· up {timeAgo(runningTask.started_at)}</span>
                  )}
                </div>
              )}

              {/* Last deployed PR */}
              {lastDeployRun && (
                <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                  <span className="text-xs text-gray-500">Deployed:</span>
                  <a
                    href={lastDeployRun.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-xs bg-white border border-gray-200 text-gray-700 rounded px-1.5 py-0.5 hover:underline"
                  >
                    {lastDeployRun.head_sha?.slice(0, 7)}
                  </a>
                  <span className="text-xs text-gray-400">{timeAgo(lastDeployRun.created_at)}</span>
                </div>
              )}
              {!lastDeployRun && lastPr && env.key === "staging" && (
                <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                  <span className="text-xs text-gray-500">Last merge:</span>
                  <a href={lastPr.url} target="_blank" rel="noreferrer" className="text-xs text-gray-700 hover:underline truncate max-w-[140px]">
                    #{lastPr.number} {lastPr.title}
                  </a>
                </div>
              )}

              {/* Links */}
              <div className="flex gap-2 flex-wrap mt-1">
                <a href={env.appUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50">App</a>
                <a href={env.apiUrl + "/docs"} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-purple-200 bg-white px-2.5 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50">API</a>
                <a href={env.logsUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50">Logs</a>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Tabs ── */}
      <div className="rounded-lg bg-white border border-gray-200 shadow">
        {/* Tab nav */}
        <div className="border-b border-gray-200 px-4">
          <nav className="flex gap-1 -mb-px">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-3 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
                  activeTab === t.key
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4">

          {/* ── Tab: Pipeline ── */}
          {activeTab === "pipeline" && (
            <div className="space-y-3">
              <div className="mb-1">
                <h3 className="text-lg font-semibold text-gray-800">Pipeline</h3>
                <p className="text-sm text-gray-500">PR lifecycle from open to ECS live.</p>
              </div>
              {allPrs.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
                  No PRs found
                </div>
              ) : (
                <>
                  {allPrs.slice(prPage * PR_PAGE_SIZE, (prPage + 1) * PR_PAGE_SIZE).map((pr) => (
                    <PipelineCard
                      key={`${pr.repo}-${pr.number}`}
                      pr={pr}
                      allRuns={allRuns}
                      ecs={ecs}
                      onMerge={handleMergePr}
                      merging={mergingPr}
                    />
                  ))}
                  <Pagination
                    page={prPage}
                    pageSize={PR_PAGE_SIZE}
                    total={allPrs.length}
                    onPrev={() => setPrPage((p) => Math.max(0, p - 1))}
                    onNext={() => setPrPage((p) => p + 1)}
                  />
                </>
              )}
            </div>
          )}

          {/* ── Tab: Quick Actions ── */}
          {activeTab === "actions" && (() => {
            // Last merged PR per repo targeting main (candidate to deploy)
            const lastMainPr = (repo) =>
              allPrs.find((p) => p.merged && p.base === "main" && p.repo === repo);

            // Last successful CI run per repo on main
            const lastCiRun = (repo) =>
              allRuns.find(
                (r) => r.repo === repo && classifyRun(r) === "ci" && r.conclusion === "success"
              );

            // Last successful deploy_preview run per repo
            const lastPreviewRun = (repo) =>
              allRuns.find(
                (r) => r.repo === repo && classifyRun(r) === "deploy_preview" && r.conclusion === "success"
              );

            const stages = [
              {
                title: "Deploy to Preview",
                subtitle: "Builds image + deploys to preview.clubsagent.ai / api-preview",
                color: "amber",
                env: "preview",
                actions: [
                  { ...DISPATCH_ACTIONS[0], repo: "backend" },
                  { ...DISPATCH_ACTIONS[1], repo: "frontend" },
                ],
                getContext: (repo) => {
                  const pr = lastMainPr(repo);
                  const ci = lastCiRun(repo);
                  return { pr, ci, lastRun: lastPreviewRun(repo) };
                },
              },
              {
                title: "Promote to Production",
                subtitle: "Promotes the current preview image to production",
                color: "red",
                env: "prod",
                actions: [
                  { ...DISPATCH_ACTIONS[2], repo: "backend" },
                  { ...DISPATCH_ACTIONS[3], repo: "frontend" },
                ],
                getContext: (repo) => {
                  const pr = lastMainPr(repo);
                  const lastRun = lastPreviewRun(repo);
                  return { pr, ci: null, lastRun };
                },
              },
            ];

            // What SHA is actually deployed to each env, per repo.
            // Backend = live ECS image tag; frontend (S3/CloudFront) = last successful deploy-run SHA.
            const lastProdRun = (repo) =>
              allRuns.find((r) => r.repo === repo && classifyRun(r) === "promote_prod" && r.conclusion === "success");
            const deployedSha = (repo, envKey) => {
              if (repo === "backend") return ecs?.[envKey]?.tasks?.[0]?.image_tag || null;
              const run = envKey === "preview" ? lastPreviewRun(repo) : lastProdRun(repo);
              return run?.head_sha?.slice(0, 7) || null;
            };

            // A currently-running deploy for this repo+stage (queued or in_progress).
            // Matched by run name so it catches both push and workflow_dispatch triggers.
            const inProgressRun = (repo, mode) =>
              allRuns.find(
                (r) =>
                  r.repo === repo &&
                  (r.status === "in_progress" || r.status === "queued") &&
                  (r.name || "").toLowerCase().includes(mode)
              );

            // Detect if develop is ahead of main.
            //
            // Problem with timestamp-of-merge comparison: auto-sync PRs are sometimes
            // created from develop before new fixes land there, then merged to main
            // AFTER those fixes land — making main's merged_at appear more recent and
            // the panel wrongly shows "in sync".
            //
            // Fix: when the latest main PR is a sync PR, use its created_at (when the
            // branch was cut from develop) as the cutoff, not its merged_at. Any develop
            // PR merged after the sync branch was cut is genuinely missing from main.
            const latestMerged = (repo, base) =>
              allPrs
                .filter((p) => p.merged && p.base === base && p.repo === repo)
                .reduce((best, p) =>
                  !best || new Date(p.merged_at) > new Date(best.merged_at) ? p : best, null);

            const developAhead = ["backend", "frontend"].filter((repo) => {
              const lastToDevelop = latestMerged(repo, "develop");
              const lastToMain = latestMerged(repo, "main");
              if (!lastToDevelop) return false;
              if (!lastToMain) return true;
              // For sync PRs (head = sync/develop-to-main or title starts with "sync:"),
              // use created_at (branch cut time) as the cutoff instead of merged_at.
              const isSyncPr =
                lastToMain.head === "sync/develop-to-main" ||
                lastToMain.title?.toLowerCase().startsWith("sync:");
              const mainCutoff = isSyncPr && lastToMain.created_at
                ? new Date(lastToMain.created_at)
                : new Date(lastToMain.merged_at);
              return new Date(lastToDevelop.merged_at) > mainCutoff;
            });

            return (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Quick Actions</h3>
                  <p className="text-sm text-gray-500">Trigger GitHub Actions on <code className="rounded bg-gray-100 px-1 text-xs">main</code>. Review what's ready before dispatching.</p>
                </div>

                {developAhead.length === 0 && !!status && (
                  <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-3">
                    <span className="text-green-500 text-lg">✅</span>
                    <div>
                      <p className="text-sm font-semibold text-green-800">develop and main are in sync</p>
                      <p className="text-xs text-green-700 mt-0.5">All merged PRs from <code className="rounded bg-green-100 px-1">develop</code> have reached <code className="rounded bg-green-100 px-1">main</code>. Ready to deploy to preview or promote to production.</p>
                    </div>
                  </div>
                )}

                {developAhead.length > 0 && (
                  <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 space-y-2">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 text-orange-500">⚠️</span>
                      <div>
                        <p className="text-sm font-semibold text-orange-800">
                          develop is ahead of main —{" "}
                          {developAhead.join(" & ")} not yet synced
                        </p>
                        <p className="text-xs text-orange-700 mt-0.5">
                          Merged PRs in <code className="rounded bg-orange-100 px-1">develop</code> haven't reached <code className="rounded bg-orange-100 px-1">main</code> yet. Create a sync PR, merge it on GitHub, then deploy. This banner clears once main is up to date.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-7">
                      {developAhead.map((repo) => {
                        const syncState = syncPrStates[repo];
                        const syncUrl = syncPrUrls[repo];
                        const isPending = syncPrPending[repo];
                        return (
                          <div key={repo} className="flex items-center gap-2">
                            <button
                              onClick={() => handleSyncPr(repo)}
                              disabled={syncState === "loading"}
                              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                                syncState === "loading"
                                  ? "bg-orange-200 text-orange-500 cursor-wait"
                                  : syncState === "done"
                                  ? isPending
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-green-100 text-green-700"
                                  : syncState === "error"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-orange-500 text-white hover:bg-orange-600"
                              }`}
                            >
                              {syncState === "loading"
                                ? "Creating…"
                                : syncState === "done"
                                ? isPending
                                  ? "⏳ Workflow running"
                                  : "✓ Created"
                                : syncState === "error"
                                ? "Failed — retry"
                                : `Create sync PR (${repo})`}
                            </button>
                            {syncUrl && (
                              <a
                                href={syncUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-xs font-semibold underline ${
                                  isPending
                                    ? "text-yellow-700 hover:text-yellow-900"
                                    : "text-blue-600 hover:text-blue-800"
                                }`}
                              >
                                {isPending ? "Check PRs in ~1 min →" : "View PR →"}
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}


                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-3">
                    <div className="text-sm font-bold text-blue-900">Step 0 · Snapshot prod DB</div>
                    <div className="text-xs text-blue-800 mt-0.5">
                      Required before <strong>Deploy Preview</strong> when the release includes migrations or risky data changes.
                      Preview and prod share the same Aurora database.
                    </div>
                  </div>

                  <div className="rounded-lg border border-blue-100 bg-white p-3 space-y-3">
                    <label className="block text-xs font-medium text-gray-700">
                      Snapshot label
                      <input
                        type="text"
                        value={dbSnapshotLabel}
                        onChange={(e) => setDbSnapshotLabel(e.target.value)}
                        placeholder="predeploy"
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </label>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleCreateDbSnapshot}
                        disabled={dbSnapshotLoading || dbSnapshot?.status === "creating"}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all ${
                          dbSnapshotLoading || dbSnapshot?.status === "creating"
                            ? "bg-blue-300 cursor-wait"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                      >
                        {dbSnapshotLoading || dbSnapshot?.status === "creating"
                          ? "Creating snapshot…"
                          : "Create DB snapshot"}
                      </button>
                      <a
                        href="https://eu-west-2.console.aws.amazon.com/rds/home?region=eu-west-2#snapshots-list:"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50"
                      >
                        Open RDS snapshots →
                      </a>
                    </div>

                    {dbSnapshot?.snapshot_id && (
                      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-1">
                        <div>
                          <span className="font-semibold">Snapshot:</span>{" "}
                          <span className="font-mono break-all">{dbSnapshot.snapshot_id}</span>
                        </div>
                        <div>
                          <span className="font-semibold">Status:</span>{" "}
                          <span className={
                            dbSnapshot.status === "available"
                              ? "text-emerald-700 font-semibold"
                              : dbSnapshot.status === "failed"
                              ? "text-red-700 font-semibold"
                              : "text-amber-700 font-semibold"
                          }>
                            {dbSnapshot.status || "unknown"}
                          </span>
                          {dbSnapshot.percent_progress != null && dbSnapshot.status === "creating" && (
                            <span className="text-gray-500"> · {dbSnapshot.percent_progress}%</span>
                          )}
                        </div>
                        {dbSnapshot.status === "available" && (
                          <div className="text-emerald-700">Safe to continue with Deploy Preview.</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {stages.map((stage) => (
                  <div key={stage.title} className={`rounded-xl border p-4 ${stage.color === "red" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
                    <div className="mb-3">
                      <div className={`text-sm font-bold ${stage.color === "red" ? "text-red-800" : "text-amber-800"}`}>{stage.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{stage.subtitle}</div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      {stage.actions.map((action) => {
                        const key = `${action.repo}-${action.workflow}-${JSON.stringify(action.inputs)}`;
                        const state = actionStates[key];
                        const { pr, lastRun } = stage.getContext(action.repo);
                        const previewSha = deployedSha(action.repo, "preview");
                        const prodSha = deployedSha(action.repo, "prod");
                        const mode = stage.env === "preview" ? "deploy_preview" : "promote_prod";
                        const running = inProgressRun(action.repo, mode);

                        return (
                          <div key={key} className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                            {/* Repo header */}
                            <RepoBadge repo={action.repo} />

                            {/* Last merged PR */}
                            {pr ? (
                              <div className="space-y-0.5">
                                <div className="text-xs text-gray-500">Last merged PR</div>
                                <a
                                  href={pr.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm font-medium text-gray-800 hover:text-blue-600 hover:underline line-clamp-1"
                                  title={pr.title}
                                >
                                  #{pr.number} {pr.title}
                                </a>
                                <div className="text-xs text-gray-400">by {pr.author} · {timeAgo(pr.updated_at)}</div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">No merged PR found on main</div>
                            )}

                            {/* Live deploy state (BE = ECS image, FE = last deploy-run SHA) */}
                            {stage.env === "preview" && (
                              <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs space-y-0.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-500">Preview now</span>
                                  <span className="font-mono text-gray-700">{previewSha || "—"}</span>
                                </div>
                                {lastRun ? (
                                  <a href={lastRun.url} target="_blank" rel="noreferrer" className="block text-gray-400 hover:underline">
                                    deployed {timeAgo(lastRun.created_at)}
                                  </a>
                                ) : null}
                              </div>
                            )}
                            {stage.env === "prod" && (() => {
                              // Sin SHA de uno de los dos lados no se puede afirmar "behind":
                              // eso pasa cuando la lectura de ECS falla y antes se disfrazaba
                              // de "Prod behind preview" con ambos valores en "—".
                              const unknown = !prodSha || !previewSha;
                              const match = !unknown && prodSha === previewSha;
                              const box = unknown
                                ? "border-gray-200 bg-gray-50"
                                : match
                                ? "border-emerald-200 bg-emerald-50"
                                : "border-amber-200 bg-amber-50";
                              return (
                                <div className={`rounded-md border px-2 py-1.5 text-xs space-y-0.5 ${box}`}>
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-500">Prod now</span>
                                    <span className="font-mono text-gray-700">{prodSha || "—"}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-500">Preview has</span>
                                    <span className="font-mono text-gray-700">{previewSha || "—"}</span>
                                  </div>
                                  <div className={`pt-0.5 font-medium ${unknown ? "text-gray-500" : match ? "text-emerald-700" : "text-amber-700"}`}>
                                    {unknown
                                      ? "ECS status unavailable — deployed image unknown"
                                      : match
                                      ? "✓ Prod matches preview"
                                      : "⚠ Prod behind preview — promote to update"}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Live "in progress" indicator (survives refresh — reads workflow status) */}
                            {running && (
                              <a
                                href={running.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-medium text-blue-700 hover:underline"
                              >
                                <HiOutlineRefresh className="animate-spin w-3.5 h-3.5" />
                                {mode === "promote_prod" ? "Promotion" : "Deploy"} in progress — view run ↗
                              </a>
                            )}

                            {/* Dispatch button */}
                            <button
                              onClick={() => handleDispatch(action)}
                              disabled={state === "loading" || !!running}
                              className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${
                                stage.color === "red"
                                  ? "border-red-500 bg-red-500 text-white hover:bg-red-600"
                                  : "border-amber-500 bg-amber-500 text-white hover:bg-amber-600"
                              }`}
                            >
                              {running ? (
                                <><HiOutlineRefresh className="animate-spin w-4 h-4" /> In progress…</>
                              ) : state === "loading" ? (
                                <><HiOutlineRefresh className="animate-spin w-4 h-4" /> Dispatching…</>
                              ) : state === "done" ? (
                                "✓ Dispatched"
                              ) : (
                                action.label
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ── Tab: CI Runs ── */}
          {activeTab === "runs" && (
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">CI Runs</h3>
                <p className="text-sm text-gray-500">Recent workflow runs across both repos.</p>
              </div>
              {allRuns.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
                  No runs
                </div>
              ) : (
                <>
                  <div className="overflow-hidden rounded-xl border border-gray-200">
                    <table className="w-full text-sm">
                      <TableHead>
                        <Th>Repo</Th>
                        <Th>Workflow</Th>
                        <Th>Branch</Th>
                        <Th>Status</Th>
                        <Th>Duration</Th>
                        <Th>Triggered</Th>
                        <Th />
                      </TableHead>
                      <tbody>
                        {allRuns.slice(runPage * RUN_PAGE_SIZE, (runPage + 1) * RUN_PAGE_SIZE).map((run) => (
                          <Tr key={`${run.repo}-${run.id}`}>
                            <Td className="px-4 py-3"><RepoBadge repo={run.repo} /></Td>
                            <Td className="max-w-[200px] truncate px-4 py-3" title={run.name}>{run.name}</Td>
                            <Td className="px-4 py-3 font-mono text-[var(--muted-foreground)]">{run.head_branch}</Td>
                            <Td className="px-4 py-3"><StatusBadge conclusion={run.conclusion} status={run.status} /></Td>
                            <Td className="px-4 py-3 text-[var(--muted-foreground)]">{formatDuration(run.created_at, run.updated_at)}</Td>
                            <Td className="px-4 py-3 text-[var(--muted-foreground)]">{timeAgo(run.created_at)}</Td>
                            <Td className="px-4 py-3">
                              <a href={run.url} target="_blank" rel="noreferrer"
                                className="font-medium text-[var(--primary)] hover:underline">
                                View
                              </a>
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={runPage}
                    pageSize={RUN_PAGE_SIZE}
                    total={allRuns.length}
                    onPrev={() => setRunPage((p) => Math.max(0, p - 1))}
                    onNext={() => setRunPage((p) => p + 1)}
                  />
                </>
              )}
            </div>
          )}

          {/* ── Tab: Environments ── */}
          {activeTab === "envs" && (
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Environments</h3>
                <p className="text-sm text-gray-500">Backend (ECS) and frontend (S3) status per environment.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {ENVS.map((env) => {
                  const svc = ecs[env.key];
                  const health = ecsHealth(svc);

                  // Last successful FE deploy run for this env
                  const feDeployStage = env.key === "prod" ? "promote_prod" : env.key === "preview" ? "deploy_preview" : null;
                  const feRun = feDeployStage
                    ? allRuns.find(
                        (r) =>
                          r.repo === "frontend" &&
                          classifyRun(r) === feDeployStage &&
                          r.conclusion === "success"
                      )
                    : null;

                  // Last successful BE deploy run for this env
                  const beRun = feDeployStage
                    ? allRuns.find(
                        (r) =>
                          r.repo === "backend" &&
                          classifyRun(r) === feDeployStage &&
                          r.conclusion === "success"
                      )
                    : null;

                  return (
                    <div key={env.key} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-lg font-semibold text-gray-800">{env.label}</span>
                        {env.key !== "staging" && (
                          <span className={`text-sm font-semibold ${health.cls}`}>{health.label}</span>
                        )}
                      </div>

                      {/* Backend */}
                      <div className="mb-3">
                        <div className="text-xs uppercase tracking-wide text-purple-600 font-semibold mb-1.5">Backend (ECS)</div>
                        {svc && !svc.error ? (
                          <div className="space-y-2">
                            <div className="grid gap-2 grid-cols-2">
                              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                <div className="text-xs uppercase tracking-wide text-gray-500">Tasks</div>
                                <div className="mt-1 text-sm font-semibold text-gray-800">{svc.running_count}/{svc.desired_count}</div>
                              </div>
                              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                <div className="text-xs uppercase tracking-wide text-gray-500">Last Deploy</div>
                                <div className="mt-1 text-sm font-semibold text-gray-800">
                                  {beRun ? (
                                    <a href={beRun.url} target="_blank" rel="noreferrer" className="hover:underline text-emerald-700">
                                      {timeAgo(beRun.created_at)}
                                    </a>
                                  ) : (svc.deployment_status || "—")}
                                </div>
                              </div>
                            </div>
                            {/* Running tasks */}
                            {svc.tasks && svc.tasks.length > 0 && svc.tasks.map((t) => (
                              <div key={t.task_id} className="rounded-lg border border-gray-200 bg-white px-3 py-2 space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-mono text-xs text-gray-500 truncate max-w-[120px]">{t.task_id}</span>
                                  <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${t.health === "HEALTHY" ? "bg-emerald-100 text-emerald-700" : t.health === "UNHEALTHY" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500"}`}>{t.health}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-400">Image:</span>
                                  <span className="font-mono text-xs text-indigo-700 bg-indigo-50 rounded px-1">{t.image_tag || "—"}</span>
                                </div>
                                {t.started_at && (
                                  <div className="text-xs text-gray-400">Up {timeAgo(t.started_at)}</div>
                                )}
                              </div>
                            ))}
                            {/* Last event */}
                            {svc.last_event && (
                              <div className="text-xs text-gray-400 truncate" title={svc.last_event}>{svc.last_event}</div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">
                            {env.key === "staging" ? "Dokploy — no ECS data" : svc?.error || "no data"}
                          </p>
                        )}
                      </div>

                      {/* Frontend */}
                      <div className="mb-3">
                        <div className="text-xs uppercase tracking-wide text-indigo-600 font-semibold mb-1.5">Frontend</div>
                        {env.key === "staging" ? (
                          <p className="text-sm text-gray-400">Dokploy auto-deploy</p>
                        ) : feRun ? (
                          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                            <div className="text-xs uppercase tracking-wide text-gray-500">Last Deploy</div>
                            <div className="mt-1 text-sm font-semibold">
                              <a href={feRun.url} target="_blank" rel="noreferrer" className="hover:underline text-emerald-700">
                                {timeAgo(feRun.created_at)}
                              </a>
                              <span className="ml-2 font-mono text-xs text-gray-400">{feRun.head_sha?.slice(0, 7)}</span>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">No deploy found</p>
                        )}
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <a href={env.appUrl} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100">App</a>
                        <a href={env.apiUrl + "/docs"} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-100">API</a>
                        <a href={env.logsUrl} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50">Logs</a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Tab: AWS ── */}
          {activeTab === "aws" && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">AWS Control</h3>
                <p className="text-sm text-gray-500">Manage ECS services and tail CloudWatch logs.</p>
              </div>

              {/* Env selector */}
              <div className="flex gap-2">
                {["prod", "preview"].map((e) => (
                  <button
                    key={e}
                    onClick={() => { setAwsEnv(e); setLogs(null); setLogsError(null); }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                      awsEnv === e
                        ? "bg-gray-800 text-white border-gray-800"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {e === "prod" ? "Production" : "Preview"}
                  </button>
                ))}
              </div>

              {/* Service status card */}
              {(() => {
                const svc = ecs[awsEnv];
                const health = ecsHealth(svc);
                const task = svc?.tasks?.[0];
                return (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-500">{awsEnv === "prod" ? "Production" : "Preview"} · ECS Service</div>
                        <div className={`text-xl font-bold mt-0.5 ${health.cls}`}>{health.label}</div>
                      </div>
                      {svc && !svc.error && (
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-800">{svc.running_count}/{svc.desired_count}</div>
                          <div className="text-xs text-gray-400">tasks running</div>
                        </div>
                      )}
                    </div>

                    {task && (
                      <div className="grid gap-2 sm:grid-cols-3">
                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                          <div className="text-xs uppercase tracking-wide text-gray-500">Task ID</div>
                          <div className="mt-1 font-mono text-xs text-gray-700 truncate">{task.task_id}</div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                          <div className="text-xs uppercase tracking-wide text-gray-500">Image</div>
                          <div className="mt-1 font-mono text-xs text-indigo-700">{task.image_tag || "—"}</div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                          <div className="text-xs uppercase tracking-wide text-gray-500">Uptime</div>
                          <div className="mt-1 text-sm font-semibold text-gray-700">{task.started_at ? timeAgo(task.started_at) : "—"}</div>
                        </div>
                      </div>
                    )}

                    {svc?.last_event && (
                      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500 truncate" title={svc.last_event}>
                        {svc.last_event}
                      </div>
                    )}

                    {/* Controls */}
                    <div className="flex gap-2 flex-wrap pt-1">
                      <button
                        onClick={() => handleEcsAction("restart", { env: awsEnv })}
                        disabled={awsActionState["restart"] === "loading"}
                        className="inline-flex items-center gap-2 rounded-lg border border-amber-500 bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50 transition"
                      >
                        {awsActionState["restart"] === "loading" ? (
                          <><HiOutlineRefresh className="animate-spin w-4 h-4" /> Restarting…</>
                        ) : awsActionState["restart"] === "done" ? "✓ Restarted" : "⟳ Restart Service"}
                      </button>
                      <button
                        onClick={() => handleEcsAction("scale", { env: awsEnv, desired_count: (svc?.desired_count ?? 1) + 1 })}
                        disabled={awsActionState["scale"] === "loading" || (svc?.desired_count ?? 0) >= 5}
                        className="inline-flex items-center gap-2 rounded-lg border border-blue-500 bg-blue-500 px-3 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50 transition"
                      >
                        + Scale Up
                      </button>
                      <button
                        onClick={() => handleEcsAction("scale", { env: awsEnv, desired_count: Math.max(0, (svc?.desired_count ?? 1) - 1) })}
                        disabled={awsActionState["scale"] === "loading" || (svc?.desired_count ?? 0) <= 0}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-400 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
                      >
                        − Scale Down
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* CloudWatch log viewer */}
              <div className="rounded-xl border border-gray-200 bg-gray-900 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700">
                  <span className="text-sm font-semibold text-gray-300">CloudWatch Logs · {awsEnv}</span>
                  <div className="flex gap-2">
                    {[50, 100, 200].map((n) => (
                      <button
                        key={n}
                        onClick={() => fetchLogs(awsEnv, n)}
                        disabled={logsLoading}
                        className="px-2 py-1 rounded text-xs font-medium text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-40 transition"
                      >
                        {n} lines
                      </button>
                    ))}
                    <button
                      onClick={() => fetchLogs(awsEnv, logs?.events?.length || 100)}
                      disabled={logsLoading}
                      className="px-2 py-1 rounded text-xs font-medium text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-40 transition"
                    >
                      <HiOutlineRefresh className={`w-3.5 h-3.5 ${logsLoading ? "animate-spin" : ""}`} />
                    </button>
                  </div>
                </div>

                {!logs && !logsLoading && !logsError && (
                  <div className="px-4 py-8 text-center text-gray-500 text-sm">
                    Click a line count above to load logs
                  </div>
                )}
                {logsLoading && (
                  <div className="px-4 py-8 text-center text-gray-500 text-sm">Loading…</div>
                )}
                {logsError && (
                  <div className="px-4 py-4 text-red-400 text-sm">{logsError}</div>
                )}
                {logs && !logsLoading && (
                  <div className="overflow-auto max-h-[500px] p-3 font-mono text-xs text-green-300 space-y-0.5">
                    {logs.events.length === 0 ? (
                      <div className="text-gray-500">No log events found</div>
                    ) : logs.events.map((ev, i) => {
                      const ts = ev.ts ? new Date(ev.ts).toISOString().replace("T", " ").slice(0, 23) : "";
                      const isError = /error|exception|traceback|critical/i.test(ev.msg);
                      const isWarn = /warn/i.test(ev.msg);
                      return (
                        <div key={i} className={`leading-5 ${isError ? "text-red-400" : isWarn ? "text-yellow-400" : "text-green-300"}`}>
                          <span className="text-gray-500 select-none mr-2">{ts}</span>
                          {ev.msg}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          )}

          {/* ── Tab: Tracker ── */}
          {activeTab === "tracker" && (() => {
            const filteredPrs = allPrs.filter(
              (p) => trackerRepo === "all" || p.repo === trackerRepo
            );

            const stageConfig = [
              { key: "develop", label: "Develop", icon: "🔀", color: "blue" },
              { key: "main",    label: "Main",    icon: "🌿", color: "gray" },
              { key: "preview", label: "Preview", icon: "🔭", color: "purple" },
              { key: "production", label: "Production", icon: "🚀", color: "green" },
            ];

            const stageColor = (status) => {
              if (status === "complete" || status === "success") return "text-green-600 bg-green-50 border-green-200";
              if (status === "open" || status === "in_progress") return "text-blue-600 bg-blue-50 border-blue-200";
              if (status === "failure") return "text-red-600 bg-red-50 border-red-200";
              return "text-gray-400 bg-gray-50 border-gray-200";
            };

            const stageIcon = (status) => {
              if (status === "complete" || status === "success") return "✓";
              if (status === "open" || status === "in_progress") return "⟳";
              if (status === "failure") return "✗";
              return "○";
            };

            const checkColor = (conclusion) => {
              if (conclusion === "success") return "text-green-600";
              if (conclusion === "failure" || conclusion === "cancelled") return "text-red-500";
              if (!conclusion) return "text-yellow-500";
              return "text-gray-400";
            };

            const fmtTime = (iso) =>
              iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

            return (
              <div className="flex gap-4 h-full min-h-0" style={{ minHeight: 500 }}>

                {/* ── PR list ── */}
                <div className="w-72 flex-shrink-0 flex flex-col gap-2">
                  {/* Repo filter */}
                  <div className="flex gap-1 rounded-xl bg-gray-100 p-1">
                    {[["all","All"], ["backend","BE"], ["frontend","FE"]].map(([k, lbl]) => (
                      <button key={k} onClick={() => setTrackerRepo(k)}
                        className={`flex-1 rounded-lg py-1 text-xs font-semibold transition-all ${trackerRepo === k ? "bg-white shadow text-gray-800" : "text-gray-500"}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>

                  {/* PR rows */}
                  <div className="flex-1 overflow-y-auto space-y-1.5 pr-0.5">
                    {filteredPrs.length === 0 && (
                      <p className="text-xs text-gray-400 text-center pt-6">No PRs found</p>
                    )}
                    {filteredPrs.map((pr) => (
                      <button key={`${pr.repo}-${pr.number}`} onClick={() => fetchLifecycle(pr)}
                        className={`w-full text-left rounded-xl border px-3 py-2.5 transition-all hover:shadow-sm ${
                          trackerPr?.number === pr.number && trackerPr?.repo === pr.repo
                            ? "border-indigo-300 bg-indigo-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${pr.repo === "backend" ? "bg-violet-100 text-violet-700" : "bg-cyan-100 text-cyan-700"}`}>
                            {pr.repo === "backend" ? "BE" : "FE"}
                          </span>
                          <span className="text-[10px] font-mono text-gray-400">#{pr.number}</span>
                          <span className={`ml-auto rounded px-1.5 py-0.5 text-[10px] font-semibold ${pr.merged ? "bg-purple-100 text-purple-700" : pr.state === "open" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {pr.merged ? "merged" : pr.state}
                          </span>
                        </div>
                        <p className="text-xs font-medium text-gray-700 line-clamp-2 leading-tight">{pr.title}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{pr.author} · {fmtTime(pr.updated_at)}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Lifecycle panel ── */}
                <div className="flex-1 flex flex-col gap-4 min-w-0 overflow-y-auto">
                  {!trackerPr && (
                    <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                      <p className="text-4xl mb-3">←</p>
                      <p className="text-sm">Select a PR to see its lifecycle</p>
                    </div>
                  )}

                  {trackerPr && trackerLoading && (
                    <div className="flex items-center justify-center h-48 text-gray-400">
                      <div className="animate-spin text-3xl">⟳</div>
                      <span className="ml-3 text-sm">Loading lifecycle…</span>
                    </div>
                  )}

                  {trackerPr && !trackerLoading && trackerLifecycle && (() => {
                    const lc = trackerLifecycle;
                    const isSyncPr = lc.head === "sync/develop-to-main" || lc.title?.toLowerCase().startsWith("sync: develop");
                    return (
                      <>
                        {/* PR header */}
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`rounded px-2 py-0.5 text-xs font-bold ${lc.repo === "backend" ? "bg-violet-100 text-violet-700" : "bg-cyan-100 text-cyan-700"}`}>
                                  {lc.repo === "backend" ? "BE" : "FE"}
                                </span>
                                <span className="text-xs text-gray-400 font-mono">#{lc.number}</span>
                                <a href={lc.url} target="_blank" rel="noreferrer"
                                  className="text-xs text-indigo-600 hover:underline ml-auto">↗ GitHub</a>
                              </div>
                              <h3 className="text-sm font-semibold text-gray-800 mt-1">{lc.title}</h3>
                              <p className="text-xs text-gray-500 mt-0.5">
                                by <strong>{lc.author}</strong> · base: <code className="rounded bg-gray-100 px-1">{lc.base}</code>
                                {lc.merged_at && ` · merged ${fmtTime(lc.merged_at)}`}
                              </p>
                              <p className="text-[10px] font-mono text-gray-400 mt-0.5">SHA: {lc.head_sha?.slice(0,10)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Stage pipeline */}
                        <div className="grid grid-cols-4 gap-2">
                          {stageConfig.map((sc, idx) => {
                            const stage = lc.stages?.find((s) => s.name === sc.key) || {};
                            const isSourceStage = isSyncPr && sc.key === "develop";
                            const isDone = stage.status === "complete" || stage.status === "success";
                            const isFail = stage.status === "failure";
                            const isActive = stage.status === "open" || stage.status === "in_progress";
                            return (
                              <div key={sc.key} className={`rounded-xl border p-3 ${isSourceStage ? "text-blue-700 bg-blue-50 border-blue-200" : stageColor(stage.status)}`}>
                                <div className="flex items-center gap-1.5 mb-2">
                                  <span className="text-base">{sc.icon}</span>
                                  <span className="text-xs font-bold">{sc.label}</span>
                                  <span className="ml-auto text-sm font-bold">{isSourceStage ? "↑" : stageIcon(stage.status)}</span>
                                </div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                                  {isSourceStage ? "source" : isDone ? "complete" : isFail ? "failed" : isActive ? "in progress" : "pending"}
                                </p>
                                {isSourceStage && (
                                  <p className="text-[10px] mt-1 opacity-60">Sync PR — sourced from develop</p>
                                )}
                                {(stage.merged_at || stage.created_at) && (
                                  <p className="text-[10px] mt-1 opacity-60">{fmtTime(stage.merged_at || stage.created_at)}</p>
                                )}
                                {stage.sha && (
                                  <p className="text-[10px] font-mono mt-0.5 opacity-50">{stage.sha.slice(0,8)}</p>
                                )}
                                {stage.url && (
                                  <a href={stage.url} target="_blank" rel="noreferrer"
                                    className="text-[10px] underline opacity-60 block mt-1">View run ↗</a>
                                )}
                                {/* Connector arrow */}
                                {idx < stageConfig.length - 1 && (
                                  <div className="absolute right-0 top-1/2 translate-x-full -translate-y-1/2 text-gray-300 text-xs pointer-events-none">→</div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* CI Checks */}
                        {lc.checks?.length > 0 && (
                          <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">CI Checks (head commit)</h4>
                            <div className="space-y-1.5">
                              {lc.checks.map((c) => (
                                <div key={c.id} className="flex items-center gap-2 text-xs">
                                  <span className={`font-bold w-3 text-center ${checkColor(c.conclusion)}`}>
                                    {c.conclusion === "success" ? "✓" : c.conclusion === "failure" ? "✗" : c.status === "in_progress" ? "⟳" : "○"}
                                  </span>
                                  <span className="flex-1 text-gray-700 truncate">{c.name}</span>
                                  <span className={`text-[10px] font-semibold ${checkColor(c.conclusion)}`}>
                                    {c.conclusion || c.status}
                                  </span>
                                  {c.url && (
                                    <a href={c.url} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline text-[10px]">↗</a>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Timeline */}
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                          <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">Timeline</h4>
                          <ol className="relative border-l border-gray-200 space-y-3 pl-4">
                            <li>
                              <div className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border border-white bg-gray-300"></div>
                              <p className="text-xs text-gray-700 font-medium">PR #{lc.number} opened</p>
                              <p className="text-[10px] text-gray-400">{fmtTime(lc.created_at)} · {lc.author}</p>
                            </li>
                            {lc.stages?.filter((s) => s.merged_at || s.created_at).map((s) => (
                              <li key={s.name}>
                                <div className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full border border-white ${s.status === "complete" || s.status === "success" ? "bg-green-400" : s.status === "failure" ? "bg-red-400" : "bg-yellow-300"}`}></div>
                                <p className="text-xs text-gray-700 font-medium capitalize">{s.name} — {s.status}</p>
                                <p className="text-[10px] text-gray-400">{fmtTime(s.merged_at || s.created_at)}{s.sha ? ` · ${s.sha.slice(0,8)}` : ""}</p>
                              </li>
                            ))}
                          </ol>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
}
